// ** MUI Imports
import { styled, useTheme } from '@mui/material/styles';
import MuiAppBar, { type AppBarProps } from '@mui/material/AppBar';
import MuiToolbar, { type ToolbarProps } from '@mui/material/Toolbar';
import { type Theme } from '@mui/material/styles';

// ** Type Imports
import type { LayoutProps } from '../../../../types';

// ** Util Import
import { hexToRGBA } from '@erikdakoda/ui/shared/colorUtils';

interface Props {
  hidden: LayoutProps['hidden'];
  toggleNavVisibility: () => void;
  settings: LayoutProps['settings'];
  saveSettings: LayoutProps['saveSettings'];
  appBarContent: NonNullable<LayoutProps['verticalLayoutProps']['appBar']>['content'];
  appBarProps: NonNullable<LayoutProps['verticalLayoutProps']['appBar']>['componentProps'];
}

const Toolbar = styled(MuiToolbar)<ToolbarProps>(({ theme }) => ({
  width: '100%',
  borderBottomLeftRadius: 10,
  borderBottomRightRadius: 10,
  padding: `${theme.spacing(0)} !important`,
  transition:
    'padding .25s ease-in-out, box-shadow .25s ease-in-out, backdrop-filter .25s ease-in-out',
}));

const LayoutAppBar = (props: Props) => {
  // ** Props
  const { settings, appBarProps, appBarContent: userAppBarContent } = props;

  // ** Hooks
  const theme = useTheme();

  // ** Vars
  const { skin, appBar, contentWidth } = settings;

  if (appBar === 'hidden') {
    return null;
  }

  let userAppBarStyle = {};
  if (appBarProps && appBarProps.sx) {
    userAppBarStyle = appBarProps.sx;
  }
  const userAppBarProps = Object.assign({}, appBarProps);
  delete userAppBarProps.sx;

  const AppBar = styled(MuiAppBar)<AppBarProps>(({ theme }) => ({
    transition:
      'border-bottom 0.2s ease-in-out, backdrop-filter .25s ease-in-out, box-shadow .25s ease-in-out',
    ...(appBar === 'fixed'
      ? theme.palette.background.filter && {
          backdropFilter: theme.palette.background.filter,
          backgroundColor: (theme: Theme) => hexToRGBA(theme.palette.background.paper, 0.85),
        }
      : {}),
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(0, 6),
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    ...(appBar === 'static' && { zIndex: 13 }),
    ...(skin === 'bordered' && {
      borderBottom: `1px solid ${theme.palette.divider}`,
    }),
    ...userAppBarStyle,
    [theme.breakpoints.down('sm')]: {
      paddingLeft: theme.spacing(4),
      paddingRight: theme.spacing(4),
    },
    paddingTop: 'env(safe-area-inset-top)',
  }));

  return (
    <AppBar
      color="default"
      elevation={skin === 'bordered' ? 0 : 5}
      className="layout-navbar-vertical"
      position={appBar === 'fixed' ? 'sticky' : 'static'}
      {...userAppBarProps}
    >
      <Toolbar
        className="navbar-content-container"
        sx={{
          mx: 'auto',
          ...(contentWidth === 'boxed' && {
            '@media (min-width:1440px)': { maxWidth: `calc(1440px - ${theme.spacing(6)} * 2)` },
          }),
        }}
      >
        {(userAppBarContent && userAppBarContent(props)) || null}
      </Toolbar>
    </AppBar>
  );
};

export default LayoutAppBar;
